import { rem } from 'polished'
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import SaveButtonOnboardingTooltip from './CuratedTrip/SaveButtonOnboardingTooltip'
import CopyTemplate from './TripModal/Templates/CopyTemplate'

import {
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineHeartIcon from 'components/Luxkit/Icons/line/LineHeartIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import { DEFAULT_TRIP_IMAGE } from 'tripPlanner/config'
import { HeaderInViewContext } from 'tripPlanner/contexts/HeaderInViewContext'
import useSaveButtonOnboardingTooltipRules from 'tripPlanner/hooks/onboarding/useSaveButtonOnboardingTooltipRules'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { FullTrip } from 'tripPlanner/types/common'

const BottomContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  z-index: 1;
`

const ImageWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const DarkOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 100%
  );
`

const TripHeaderCard = styled.div`
  position: relative;
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: ${rem(180)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)} ${rem(40)};
    height: ${rem(220)};
  }
`

interface Props {
  trip: FullTrip
  descriptors: Array<string>
}

const trackTripLoginModalSignUpView = () => {
  fireInteractionEvent(tripLoginModalSignUpView('save_curated_trip'))
}
const trackTripLoginModalSignUpDismiss = () => {
  fireInteractionEvent(tripLoginModalSignUpDismiss('save_curated_trip'))
}

function CuratedTripHeader(props: Props) {
  const { trip, descriptors } = props
  const openModal = useOpenModal()

  const { isTooltipOpen, setTooltipWasDismissed } =
    useSaveButtonOnboardingTooltipRules()

  const saveButtonRef = useRef<HTMLButtonElement | null>(null)

  const headerInViewContext = useContext(HeaderInViewContext)
  const [headerCardRef, headerInView] = useInView({
    triggerOnce: false, // Load it once and keep it loaded
    rootMargin: '-100px',
  })

  const onSave = useCallback(
    () =>
      openModal(CopyTemplate, {
        template: trip,
      }),
    [openModal, trip],
  )

  const onSaveClick = usePendingLoginHandler(
    onSave,
    'tripPlannerLogin',
    trackTripLoginModalSignUpView,
    trackTripLoginModalSignUpDismiss,
  )

  useEffect(() => {
    headerInViewContext.setInView(headerInView)
  }, [headerInViewContext, headerInView])

  const tripImage = useMemo(
    (): App.Image => ({
      id: trip.imageId,
      url: trip.imageId ? undefined : DEFAULT_TRIP_IMAGE,
      title: trip.name,
    }),
    [trip],
  )

  return (
    <TripHeaderCard ref={headerCardRef}>
      <ImageWrapper>
        <Image image={tripImage} fit="center" dpr={2} height={220} />
        <DarkOverlay />
      </ImageWrapper>
      <BottomContent>
        <VerticalSpacer gap={8}>
          <Heading wrap="word-wrap" colour="neutral-eight" variant="heading2">
            {trip.name}
          </Heading>
          <BodyText colour="neutral-eight" variant="medium" weight="bold">
            {descriptors.map((d, i) => (
              <Fragment key={i}>
                {i > 0 && <span>&nbsp;&nbsp;·&nbsp;&nbsp;</span>}
                <span>{d}</span>
              </Fragment>
            ))}
          </BodyText>
        </VerticalSpacer>
        <CSSBreakpoint min="desktop">
          <TextButton
            onClick={onSaveClick}
            shape="round"
            startIcon={<LineHeartIcon />}
            kind="primary"
            variant="ghost"
            ref={saveButtonRef}
          >
            Save trip
          </TextButton>
          {headerInViewContext.inView && (
            <SaveButtonOnboardingTooltip
              isOpen={isTooltipOpen}
              anchorRef={saveButtonRef}
              onDismiss={setTooltipWasDismissed}
              placement="bottom"
            />
          )}
        </CSSBreakpoint>
      </BottomContent>
    </TripHeaderCard>
  )
}

export default CuratedTripHeader
